import React from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import _, { lowerCase } from "lodash";
import {
    useCreateDashboardStore,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
} from ".";
import CreateChartModal from "./modal/CreateChart";
import useOrganization from "hooks/organization";
import { ColorPicker } from "components/dashboard/create";
import { BreakLine } from "components/common";
import {
    getChartSourceFromWidgetType,
    getWidgetConfigByWidgetType,
    WidgetType,
} from "../utils/widgetConfig";
import { WidgetItem } from "types";
import { CHART_DUPLICATE_COLOR_MESSAGE } from "constant";
import { parseColorToHex } from "utils/functions";

const defaultColors: Partial<Record<WidgetType, string[]>> = {
    [WidgetType.EVENT_CHART_ALERT_TRIGGER_COUNT]: ["#FDA8A8", "#D25A5A"],
    [WidgetType.EVENT_CHART_NOTIFIER_COUNT]: ["#EE9EC6", "#F6C39D", "#FFEFB5"],
};

const ColorFlag = ({ color }: { color: string }) => {
    return (
        <div
            style={{
                width: 32,
                height: 17,
                borderRadius: 2,
                backgroundColor: color,
            }}
        ></div>
    );
};

const OtherChartCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "name",
            name: "Widget Name",
        },
        {
            key: "",
            name: "Colors",
            render: (data: any) => {
                const colors = data.colors ?? [];

                return (
                    <div className="d-flex gap-2">
                        {colors.map((color: string) => (
                            <ColorFlag
                                key={`color-flag-${lowerCase(color)}`}
                                color={parseColorToHex(color)}
                            />
                        ))}
                    </div>
                );
            },
        },
    ],
    transform: (data: any, widgetType: WidgetType) => {
        const { source, chart } = getChartSourceFromWidgetType(widgetType);

        return {
            attributes: [
                {
                    organization_id: useOrganization.getState().orgId,
                    color: (data.colors ?? []).map(
                        (c: string, index: number) => [index, c]
                    ),
                },
            ],
            source: source,
            chart: chart,
            name: data.name,
        };
    },
    validate() {
        const validates = this.data.map((item) => ({
            path: "",
            isValid: CHART_NAME_PATTERN.test(item.name.trim()),
            message: CHART_ERROR_MESSAGE,
        }));

        const validateColor = () => {
            const uniqueColors = Array.from(
                new Set(
                    this.data[0].colors.map((color: string) => lowerCase(color))
                )
            );
            return {
                isValid: uniqueColors.length === this.data[0].colors.length,
                message: CHART_DUPLICATE_COLOR_MESSAGE,
            };
        };

        return [...validates, validateColor()];
    },
};

const DashboardCreateOtherComponent = ({ widget }: { widget: WidgetItem }) => {
    const widgetType = widget.widgetType;
    const widgetConfig = getWidgetConfigByWidgetType(widgetType);

    const defaultFormValues = {
        name: widgetConfig.defaultWidgetName,
        colors: defaultColors[widgetType],
    };
    const [formValues, setFormValues] = React.useState<{
        [key: string]: any;
    }>({
        ...defaultFormValues,
    });
    const { createModalShow, setCreateModalShow, createData } =
        useCreateDashboardStore();

    React.useEffect(() => {
        useCreateDashboardStore.setState({
            createData: {
                ...OtherChartCreateData,
                data: [{ ...formValues }],
            },
        });
    }, [formValues]);

    const handleFormChange = ({
        event,
        field,
        value,
    }: {
        event?: React.ChangeEvent<any>;
        field?: string;
        value?: any;
    }) => {
        setCreateModalShow(false); // Legacy code, don't know why it's here

        const values: { [key: string]: any } = {};
        if (event) {
            const { name, type, checked, value } = event.target;
            values[name] = type === "checkbox" ? checked : value;
        } else if (field) {
            values[field] = value;
        }

        const newFormValues = {
            ...formValues,
            ...values,
        };
        setFormValues(newFormValues);
    };

    return (
        <React.Fragment>
            <Row className="mt-4">
                <Col sm={12}>
                    <Form onChange={(event) => handleFormChange({ event })}>
                        <Form.Group>
                            <Form.Label htmlFor="name">Widget Name</Form.Label>
                            <Form.Control
                                id="name"
                                name="name"
                                type="text"
                                placeholder="Widget Name"
                                defaultValue={widgetConfig.defaultWidgetName}
                            />
                        </Form.Group>

                        <BreakLine className={`${"my-3"}`} />

                        <Form.Group className="mr-2">
                            <Form.Label>Colors</Form.Label>
                            <div className="d-flex gap-3 mt-2">
                                {formValues.colors.map(
                                    (color: string, index: number) => (
                                        <ColorPicker
                                            key={`${widgetType}-${index}`}
                                            aria-label="color-picker"
                                            defaultValue={color}
                                            onChange={(e, value) => {
                                                const newColors = [
                                                    ...formValues.colors,
                                                ];
                                                newColors[index] = value;

                                                handleFormChange({
                                                    field: "colors",
                                                    value: newColors,
                                                });
                                            }}
                                        />
                                    )
                                )}
                            </div>
                        </Form.Group>
                    </Form>
                </Col>
            </Row>
            <CreateChartModal
                show={createModalShow && !_.isEmpty(createData)}
                onHide={() => setCreateModalShow(false)}
            />
        </React.Fragment>
    );
};

export default React.memo(DashboardCreateOtherComponent);
